import { CreateFocusCss } from '../../../styles'
import fontFamilies from '../../../theming/docusign-themes/olive/fontFamilies'
import type { Tokens } from '../../../theming/types'
import type { ButtonProps } from '..'
import type { KindStyles } from './ink'

export default (config: {
  props: Pick<ButtonProps, 'disabled' | 'kind' | 'size'>
  tokens: Tokens
}) => {
  const { props, tokens } = config
  const size = props.size!
  const kind = props.kind === 'brand' ? 'main' : props.kind!

  const sizeStyles = {
    small: {
      fontSize: '11px',
      minHeight: '22px',
      minWidth: '22px',
      paddingBlock: '1px',
      paddingInline: '10px',
    },
    medium: {
      fontSize: '12px',
      minHeight: tokens.ctaSizeMd,
      minWidth: tokens.ctaSizeMd,
      paddingBlock: '4px',
      paddingInline: '14px',
    },
    large: {
      fontSize: '13px',
      minHeight: '34px',
      minWidth: '34px',
      paddingBlock: '7px',
      paddingInline: '18px',
    },
    xlarge: {
      fontSize: '13px',
      minHeight: '40px',
      minWidth: '40px',
      paddingBlock: '10px',
      paddingInline: '20px',
    },
  }

  const kindStyles: KindStyles = {
    main: {
      color: {
        backgroundColor: tokens.button.main.defaultBackgroundColor,
        color: tokens.button.main.defaultTextColor,
      },
      hover: {
        backgroundColor: tokens.button.main.hoverBackgroundColor,
        color: tokens.button.main.defaultTextColor,
      },
      active: {
        backgroundColor: tokens.button.main.activeBackgroundColor,
      },
      disabled: {
        backgroundColor: tokens.button.main.defaultBackgroundColor,
        color: tokens.button.main.defaultTextColor,
      },
      seperator: {
        borderInlineStartColor: tokens.button.main.defaultTextColor,
      },
    },
    primary: {
      color: {
        backgroundColor: tokens.button.primary.defaultBackgroundColor,
        color: tokens.button.primary.defaultTextColor,
      },
      hover: {
        backgroundColor: tokens.button.primary.hoverBackgroundColor,
        color: tokens.button.primary.defaultTextColor,
      },
      active: {
        backgroundColor: tokens.button.primary.activeBackgroundColor,
      },
      disabled: {
        backgroundColor: tokens.button.primary.defaultBackgroundColor,
        color: tokens.button.primary.defaultTextColor,
      },
      seperator: {
        borderInlineStartColor: tokens.button.primary.defaultTextColor,
      },
    },
    secondary: {
      color: {
        backgroundColor: tokens.button.secondary.defaultBackgroundColor,
        borderColor: tokens.button.secondary.defaultBorderColor,
        color: tokens.button.secondary.defaultTextColor,
      },
      hover: {
        backgroundColor: tokens.button.secondary.hoverBackgroundColor,
        color: tokens.button.secondary.defaultTextColor,
      },
      active: {
        backgroundColor: tokens.button.secondary.activeBackgroundColor,
      },
      disabled: {
        color: tokens.button.secondary.defaultTextColor,
        backgroundColor: tokens.button.secondary.defaultBackgroundColor,
      },
      icon: {
        fill: tokens.iconColorDefault,
      },
      seperator: {
        borderInlineStartColor: tokens.button.secondary.defaultTextColor,
      },
    },
    tertiary: {
      color: {
        backgroundColor: tokens.button.tertiary.defaultBackgroundColor,
        color: tokens.button.tertiary.defaultTextColor,
      },
      hover: {
        backgroundColor: tokens.button.tertiary.hoverBackgroundColor,
        color: tokens.button.tertiary.defaultTextColor,
      },
      active: {
        backgroundColor: tokens.button.tertiary.activeBackgroundColor,
      },
      disabled: {
        color: tokens.button.tertiary.defaultTextColor,
        backgroundColor: tokens.button.tertiary.defaultBackgroundColor,
      },
      icon: {
        fill: tokens.iconColorDefault,
      },
      seperator: {
        borderInlineStartColor: 'transparent',
      },
    },
    danger: {
      color: {
        backgroundColor: tokens.button.danger.defaultBackgroundColor,
        color: tokens.button.danger.defaultTextColor,
      },
      hover: {
        backgroundColor: tokens.button.danger.hoverBackgroundColor,
        color: tokens.button.danger.defaultTextColor,
      },
      active: {
        backgroundColor: tokens.button.danger.activeBackgroundColor,
      },
      disabled: {
        color: tokens.button.danger.defaultTextColor,
        backgroundColor: tokens.button.danger.defaultBackgroundColor,
      },
      seperator: {
        borderInlineStartColor: tokens.button.danger.defaultTextColor,
      },
    },
  }

  return {
    button: {
      default: {
        borderRadius: '2px',
        fontFamily: fontFamilies.MavenPro,
        letterSpacing: '0.66px',
        textTransform: 'uppercase',
        ...sizeStyles[size],
        ...kindStyles[kind].color,

        ...(kindStyles[kind].icon && {
          '& svg': { ...kindStyles[kind].icon },
        }),

        '&:hover': { ...kindStyles[kind].hover },
        '&:active, &[aria-expanded="true"]': { ...kindStyles[kind].active },
        '&:disabled': {
          opacity: tokens.opacityDisabled,
          cursor: 'not-allowed',
          ...kindStyles[kind].disabled,
        },
      },

      active: { ...kindStyles[kind].active },

      // These styles are only used in FileInput Component
      disabled: {
        '&:hover': { ...kindStyles[kind].disabled },
      },

      // These styles are only used in ButtonGroup and the ComboButton components
      buttonSeparatorColor: { ...kindStyles[kind].seperator },

      round: {
        borderRadius: '50%',
        boxShadow: tokens.elevationLow,
        height: '48px',
        minHeight: '48px',
        padding: 0,
        width: '48px',

        '&:hover': {
          boxShadow: tokens.elevationMedium,
        },
      },

      inverted: {
        ...CreateFocusCss({
          outlineColor: tokens.button.invertedBackgroundColor,
        }),

        primary: {
          backgroundColor: tokens.button.invertedBackgroundColor,
          borderColor: tokens.button.invertedBackgroundColor,
          color: tokens.button.invertedTextColor,

          '& svg': {
            fill: tokens.button.invertedTextColor,
          },

          '&:hover': {
            backgroundColor: 'transparent',
            borderColor: tokens.button.invertedBackgroundColor,
            color: tokens.button.invertedBackgroundColor,

            '& svg': {
              fill: tokens.button.invertedBackgroundColor,
            },
          },

          '&:active': {
            opacity: 0.7,
          },

          '&:disabled, &:disabled:hover': {
            opacity: 0.3,
          },
        },

        secondary: {
          backgroundColor: 'transparent',
          borderColor: tokens.button.invertedBackgroundColor,
          color: tokens.button.invertedBackgroundColor,

          '& svg': {
            fill: tokens.button.invertedBackgroundColor,
          },

          '&:hover': {
            backgroundColor: tokens.button.invertedBackgroundColor,
            color: tokens.button.invertedTextColor,

            '& svg': {
              fill: tokens.button.invertedTextColor,
            },
          },

          '&:active': {
            backgroundColor: tokens.dark.ctaBgColorSecondaryActive,
            color: tokens.dark.fontColorDefault,
            transitionDuration: '0.15s',

            '& svg': {
              fill: tokens.button.invertedBackgroundColor,
            },
          },

          '&:disabled, &:disabled:hover': {
            opacity: tokens.opacityDisabled,
          },
        },

        tertiary: {
          backgroundColor: 'transparent',
          borderColor: 'transparent',
          color: tokens.button.invertedBackgroundColor,

          '& svg': {
            fill: tokens.button.invertedBackgroundColor,
          },

          '&:hover': {
            borderColor: tokens.button.invertedBackgroundColor,
            color: tokens.button.invertedBackgroundColor,
            transition: `
              background-color 0.2s ease,
              border 0.2s ease,
              box-shadow 0.2s ease,
              color 0.2s ease
            `,

            '& svg': {
              fill: tokens.button.invertedBackgroundColor,
            },
          },

          '&:active, &[aria-expanded="true"]': {
            backgroundColor: tokens.dark.ctaBgColorTertiaryActive,
            color: tokens.dark.fontColorDefault,
            transitionDuration: '0.15s',

            '& svg': {
              fill: tokens.button.invertedBackgroundColor,
            },
          },

          '&:disabled, &:disabled:hover': {
            opacity: tokens.opacityDisabled,
          },
        },
      },

      ...(!props.disabled && {
        tertiary: {
          default: {
            '&:hover': {
              color: tokens.ctaBgColorPrimaryHover,
              '& svg': {
                fill: tokens.ctaBgColorPrimaryHover,
              },
            },
          },
        },
      }),
    },

    icon: {
      beforeText: {
        marginInline: '-4px 6px',
      },

      afterText: {
        marginInline: '6px -4px',
      },
    },

    menuTrigger: {
      paddingInlineEnd: '6px',
    },

    caret: {
      marginInlineStart: '2px',
    },

    pill: {
      borderRadius: '20px',
    },
  } as const
}
